<script lang="ts">
  import SpaceMap from './SpaceMap.svelte';
  import ClockRegular from '@cobot-assets/icons/regular/clock-regular-full.svg?component';
  import LocationDotRegular from '@cobot-assets/icons/regular/location-dot-regular-full.svg?component';
  import UserRegular from '@cobot-assets/icons/regular/user-regular-full.svg?component';
  import GlobeRegular from '@cobot-assets/icons/regular/globe-regular-full.svg?component';
  import InstagramBrands from '@cobot-assets/icons/brands/instagram-brands-solid-full.svg?component';
  import XTwitterBrands from '@cobot-assets/icons/brands/x-twitter-brands-solid-full.svg?component';
  import FacebookBrands from '@cobot-assets/icons/brands/square-facebook-brands-solid-full.svg?component';
  import LinkedinBrands from '@cobot-assets/icons/brands/linkedin-brands-solid-full.svg?component';
  import type {
    SpaceContactFooterProps,
    SpaceProfileExternalLinkType,
    SpaceProfileOpenHour,
  } from '../types/space/space_contact_footer';
  import type { Component } from 'svelte';
  import { DateTime } from 'luxon';
  import { formatTimeOfDay, type HourFormat } from '../utils/date-formats';

  let {
    name,
    profile,
    dayNames,
    closedLabel,
    mapLabel,
    websiteLabel,
    currentWeekday,
    hourFormat,
  }: SpaceContactFooterProps = $props();

  type SocialLinkConfig = {
    type: SpaceProfileExternalLinkType;
    label: string;
    icon: Component;
  };

  const structuredAddress = $derived(profile.structuredAddress);
  const street = $derived(structuredAddress?.street ?? null);
  const postCode = $derived(structuredAddress?.postCode ?? null);
  const city = $derived(structuredAddress?.city ?? null);
  const latitude = $derived(structuredAddress?.latitude ?? null);
  const longitude = $derived(structuredAddress?.longitude ?? null);
  const email = $derived(profile.contactEmail);
  const phone = $derived(profile.phone);

  const postCodeCity = $derived([postCode, city].filter(Boolean).join(' '));
  const hasMap = $derived(latitude !== null && longitude !== null);

  const googleMaps = $derived(
    profile.externalLinks.find((link) => link.type === 'googleMaps')?.url ??
      null,
  );

  const socialLinkConfigs = $derived([
    { type: 'website', label: websiteLabel, icon: GlobeRegular },
    { type: 'instagram', label: 'Instagram', icon: InstagramBrands },
    { type: 'twitter', label: 'X (Twitter)', icon: XTwitterBrands },
    { type: 'facebook', label: 'Facebook', icon: FacebookBrands },
    { type: 'linkedin', label: 'LinkedIn', icon: LinkedinBrands },
  ] satisfies SocialLinkConfig[]);

  const socialLinks = $derived(
    socialLinkConfigs.flatMap((config) => {
      const url = profile.externalLinks.find(
        (link) => link.type === config.type,
      )?.url;
      return url ? [{ url, label: config.label, icon: config.icon }] : [];
    }),
  );

  const openHoursRows = $derived(
    formatOpenHoursRows(
      profile.openHours,
      dayNames,
      closedLabel,
      currentWeekday,
      hourFormat,
    ),
  );

  function formatOpenHourTime(time: string, hourFormat: HourFormat): string {
    return formatTimeOfDay(DateTime.fromFormat(time, 'HH:mm'), hourFormat);
  }

  function formatOpenHoursRows(
    openHours: SpaceProfileOpenHour[],
    names: readonly string[],
    closed: string,
    today: number,
    hourFormat: HourFormat,
  ) {
    if (openHours.length === 0) return [];

    const rangesByWeekday: Record<number, string[]> = {};
    for (const term of openHours) {
      if (term.from == null || term.to == null) continue;
      (rangesByWeekday[term.weekday] ??= []).push(
        `${formatOpenHourTime(term.from, hourFormat)} – ${formatOpenHourTime(term.to, hourFormat)}`,
      );
    }
    if (Object.keys(rangesByWeekday).length === 0) return [];

    return [1, 2, 3, 4, 5, 6, 7].map((weekday) => ({
      day: names[weekday - 1] ?? '',
      hours: rangesByWeekday[weekday]?.join(', ') ?? closed,
      isToday: weekday === today,
    }));
  }
</script>

<footer class="space-contact-footer">
  <div class="space-contact-footer__columns">
    {#if hasMap}
      <div
        class="space-contact-footer__column space-contact-footer__column--map"
      >
        <div class="space-contact-footer__map">
          <SpaceMap
            latitude={latitude as number}
            longitude={longitude as number}
            label={mapLabel}
          />
        </div>
      </div>
    {/if}
    <div class="space-contact-footer__content">
      <div class="space-contact-footer__column">
        <span class="space-contact-footer__icon fill--meta">
          <LocationDotRegular aria-hidden="true" />
        </span>
        <div>
          <p class="b mb0">{name}</p>
          {#snippet addressLines()}
            {#if street}<p class="mb0">{street}</p>{/if}
            {#if postCodeCity}<p class="mb0">{postCodeCity}</p>{/if}
          {/snippet}
          {#if googleMaps && (street || postCodeCity)}
            <a
              href={googleMaps}
              target="_blank"
              data-testid="space-contact-footer-address-link"
            >
              {@render addressLines()}
            </a>
          {:else}
            {@render addressLines()}
          {/if}
        </div>
      </div>

      {#if openHoursRows.length > 0}
        <div class="space-contact-footer__column">
          <span class="space-contact-footer__icon fill--meta">
            <ClockRegular aria-hidden="true" />
          </span>
          <dl class="space-contact-footer__hours">
            {#each openHoursRows as row (row.day)}
              <dt class="mb0" class:b={row.isToday}>{row.day}</dt>
              <dd class="ml0 mb0" class:b={row.isToday}>{row.hours}</dd>
            {/each}
          </dl>
        </div>
      {/if}

      {#if email || phone || socialLinks.length > 0}
        <div class="space-contact-footer__column">
          <span class="space-contact-footer__icon fill--meta">
            <UserRegular aria-hidden="true" />
          </span>
          <div>
            {#if email}
              <p class="mb0"><a href="mailto:{email}">{email}</a></p>
            {/if}
            {#if phone}
              <p class="mb0"><a href="tel:{phone}">{phone}</a></p>
            {/if}
            {#if socialLinks.length > 0}
              <ul class="space-contact-footer__social list pl0 flex mb0">
                {#each socialLinks as link (link.label)}
                  <li>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={link.label}
                      class="fill--meta"
                    >
                      <link.icon aria-hidden="true" />
                      <span class="visually-hidden">{link.label}</span>
                    </a>
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
        </div>
      {/if}
    </div>
  </div>
</footer>

<style>
  /* keep consistent with .space-contact-profile */
  .space-contact-footer {
    container-type: inline-size;
    margin-top: 1.5rem;
    padding: 1.5rem;
    font-size: var(--font-size-smaller);
    border: 1px solid var(--c-border-subtle);
    border-radius: var(--border-radius);
    background-color: var(--c-background);
  }

  .space-contact-footer__columns {
    display: flex;
    flex-wrap: nowrap;
    gap: 1.5rem;
    align-items: start;
  }

  .space-contact-footer__content {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    flex: 1 1 auto;
    min-width: 0;
  }

  .space-contact-footer__column {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    flex: 0 1 auto;
    min-width: 0;
  }

  .space-contact-footer__column--map {
    flex: 1 1 12rem;
    min-width: 12rem;
  }

  @container (max-width: 540px) {
    .space-contact-footer__columns {
      flex-wrap: wrap;
    }

    .space-contact-footer__column--map,
    .space-contact-footer__content {
      flex: 1 1 100%;
      min-width: 0;
    }
  }

  .space-contact-footer__icon :global(svg) {
    width: 1rem;
    height: 1rem;
  }

  .space-contact-footer__map {
    width: 100%;
    aspect-ratio: 3 / 2;
    border-radius: var(--border-radius);
    overflow: hidden;
    flex-shrink: 0;
  }

  .space-contact-footer__hours {
    display: grid;
    grid-template-columns: auto auto;
    column-gap: 1rem;
    row-gap: 0.25rem;
    align-content: start;
    margin-block: 0;
    font-size: var(--font-size-smaller);

    dd {
      white-space: nowrap;
    }
  }

  .space-contact-footer__social {
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .space-contact-footer__social :global(svg) {
    width: 1.25rem;
    height: 1.25rem;
  }
</style>
